import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'

import {
  DisclosureChevron,
  badgeVariants,
  Button,
  Icon,
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@speaking-coach/ui'
import { cn } from '../components/ui/cn'
import { MermaidDiagram } from './mermaid-diagram'
import {
  OPERATOR_FAQ_SECTIONS,
  OPERATOR_FAQ_TOPICS,
  operatorFaqTopic,
  type OperatorFaqSectionId,
  type OperatorFaqTopicId,
} from './registry'

interface OperatorFaqContextValue {
  openFaq: (topicId?: OperatorFaqTopicId) => void
}

const OperatorFaqContext = createContext<OperatorFaqContextValue>({
  // Reusable operator components are unit-tested outside the shell. The real
  // AdminShell always provides the working Sheet controller.
  openFaq: () => undefined,
})

export function useOperatorFaq(): OperatorFaqContextValue {
  return useContext(OperatorFaqContext)
}

function Topic({
  topicId,
  initiallyOpen,
}: {
  topicId: OperatorFaqTopicId
  initiallyOpen: boolean
}): JSX.Element {
  const topic = operatorFaqTopic(topicId)
  return (
    <details
      className="group rounded-token-card border border-border-default bg-surface-card"
      open={initiallyOpen}
    >
      <summary className="flex cursor-pointer list-none items-start gap-3 px-4 py-3 text-body font-medium marker:content-none">
        <Icon name="help" className="mt-0.5 shrink-0 text-text-primary" />
        <span className="min-w-0 flex-1">{topic.question}</span>
        <DisclosureChevron
          className="mt-0.5 text-text-secondary"
        />
      </summary>
      <div className="space-y-4 border-t border-border-default px-4 py-4 text-body">
        <p>{topic.answer}</p>
        <ul className="list-disc space-y-1 pl-5 text-text-secondary">
          {topic.scenarios.map((scenario) => (
            <li key={typeof scenario === 'string' ? scenario : scenario.label}>
              {typeof scenario === 'string' ? (
                scenario
              ) : (
                <>
                  <strong className="font-medium text-text-primary">
                    {scenario.label}
                  </strong>{' '}
                  — {scenario.description}
                </>
              )}
            </li>
          ))}
        </ul>
        <div className="rounded-token-card bg-accent-soft px-3 py-2">
          <p className="text-caption font-medium text-text-primary">What to do</p>
          <p className="mt-0.5 text-body">{topic.nextAction}</p>
        </div>
        {'diagram' in topic && topic.diagram !== undefined ? (
          <MermaidDiagram diagram={topic.diagram} />
        ) : null}
      </div>
    </details>
  )
}

export function OperatorFaqProvider({
  children,
}: {
  children: ReactNode
}): JSX.Element {
  const [open, setOpen] = useState(false)
  const [selectedTopicId, setSelectedTopicId] =
    useState<OperatorFaqTopicId | null>(null)
  const [selectedSectionId, setSelectedSectionId] =
    useState<OperatorFaqSectionId | null>(null)
  const returnFocusRef = useRef<HTMLElement | null>(null)

  const openFaq = useCallback((topicId?: OperatorFaqTopicId): void => {
    returnFocusRef.current =
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null
    setSelectedTopicId(topicId ?? null)
    setSelectedSectionId(
      topicId === undefined ? null : operatorFaqTopic(topicId).sectionId,
    )
    setOpen(true)
  }, [])
  const value = useMemo(() => ({ openFaq }), [openFaq])
  const orderedTopicIds = useMemo(
    () => {
      if (selectedSectionId === null) return []
      const sectionTopicIds = OPERATOR_FAQ_TOPICS.filter(
        (topic) => topic.sectionId === selectedSectionId,
      ).map((topic) => topic.id)
      if (selectedTopicId === null) return sectionTopicIds
      return [
        selectedTopicId,
        ...sectionTopicIds.filter((id) => id !== selectedTopicId),
      ]
    },
    [selectedSectionId, selectedTopicId],
  )
  const selectedSection = OPERATOR_FAQ_SECTIONS.find(
    (section) => section.id === selectedSectionId,
  )

  return (
    <OperatorFaqContext.Provider value={value}>
      {children}
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent
          aria-describedby="operator-faq-description"
          closeLabel="Close operator FAQ"
          finalFocus={() => {
            const target = returnFocusRef.current
            return target?.isConnected ? target : true
          }}
        >
          <SheetHeader>
            <SheetTitle>Operator FAQ</SheetTitle>
            <SheetDescription id="operator-faq-description">
              Guidance for common admin workflows and decisions.
            </SheetDescription>
          </SheetHeader>
          {selectedSection !== undefined ? (
            <div
              data-slot="operator-faq-subheader"
              className="grid h-12 shrink-0 grid-cols-[3rem_1px_minmax(0,1fr)] items-stretch border-b border-border-default bg-surface-card sm:grid-cols-[3.5rem_1px_minmax(0,1fr)]"
            >
              <Button
                type="button"
                variant="ghost"
                aria-label="All sections"
                title="All sections"
                className="h-full w-full shrink-0 justify-start rounded-none px-0 pl-4 sm:pl-6"
                onClick={() => {
                  setSelectedSectionId(null)
                  setSelectedTopicId(null)
                }}
              >
                <Icon name="back" data-slot="operator-faq-back-icon" />
              </Button>
              <div
                data-slot="operator-faq-subheader-divider"
                className="w-px bg-border-default"
                aria-hidden="true"
              />
              <h2
                id={`faq-${selectedSection.id}`}
                className="flex min-w-0 items-center px-3 text-body font-medium text-text-primary"
              >
                <span data-slot="operator-faq-section-label" className="truncate">
                  {selectedSection.label}
                </span>
              </h2>
            </div>
          ) : null}
          <div data-slot="operator-faq-scroll" className="flex-1 space-y-6 overflow-y-auto bg-surface-app p-4 sm:p-6">
            {selectedSection === undefined ? (
              <section aria-labelledby="operator-faq-areas">
                <h2
                  id="operator-faq-areas"
                  className="mb-2 text-label font-semibold uppercase tracking-wide text-text-secondary"
                >
                  Browse by area
                </h2>
                <div className="space-y-2">
                  {OPERATOR_FAQ_SECTIONS.map((section) => {
                    const questionCount = OPERATOR_FAQ_TOPICS.filter(
                      (topic) => topic.sectionId === section.id,
                    ).length
                    return (
                      <Button
                        key={section.id}
                        type="button"
                        variant="secondary"
                        className="w-full justify-start gap-3 bg-surface-card"
                        onClick={() => {
                          setSelectedSectionId(section.id)
                          setSelectedTopicId(null)
                        }}
                      >
                        <Icon name="help" className="text-text-primary" />
                        <span className="min-w-0 flex-1 truncate text-left">
                          {section.label}
                        </span>
                        <span className="text-caption font-normal text-text-secondary">
                          {questionCount} {questionCount === 1 ? 'question' : 'questions'}
                        </span>
                        <Icon name="next" className="text-text-secondary" />
                      </Button>
                    )
                  })}
                </div>
              </section>
            ) : (
              <section aria-labelledby={`faq-${selectedSection.id}`}>
                <div className="space-y-3">
                  {orderedTopicIds.map((topicId) => (
                    <Topic
                      key={`${topicId}-${selectedTopicId ?? 'section'}`}
                      topicId={topicId}
                      initiallyOpen={topicId === selectedTopicId}
                    />
                  ))}
                </div>
              </section>
            )}
          </div>
        </SheetContent>
      </Sheet>
    </OperatorFaqContext.Provider>
  )
}

export function OperatorFaqTrigger({
  topicId,
  label = 'Open guide',
  iconOnly = false,
  size = 'xs',
  className,
}: {
  topicId: OperatorFaqTopicId
  label?: string
  iconOnly?: boolean
  size?: 'xs' | 'sm'
  className?: string
}): JSX.Element {
  const { openFaq } = useOperatorFaq()
  return (
    <Button
      type="button"
      variant="secondary"
      size={iconOnly ? 'icon' : size}
      aria-label={iconOnly ? label : undefined}
      title={iconOnly ? label : undefined}
      className={cn(
        'bg-surface-card text-text-primary hover:bg-surface-card-hover',
        iconOnly ? 'size-7 shrink-0' : 'gap-1.5',
        className,
      )}
      onClick={() => openFaq(topicId)}
    >
      <Icon name="help" size={size === 'sm' ? 16 : 14} className="text-text-primary" />
      {iconOnly ? null : label}
    </Button>
  )
}

export function OperatorFaqBadgeTrigger({
  topicId,
  label,
  variant = 'neutral',
  className,
}: {
  topicId: OperatorFaqTopicId
  label: string
  variant?: 'neutral' | 'danger' | 'muted'
  className?: string
}): JSX.Element {
  const { openFaq } = useOperatorFaq()
  return (
    <button
      type="button"
      aria-label={`Open guide for ${label}`}
      title={`Open guide for ${label}`}
      className={cn(
        badgeVariants({ variant }),
        // At least 24 px tall, the mouse target minimum (design tokens §7).
        'min-h-6 cursor-pointer transition-colors',
        variant === 'danger'
          ? 'hover:border-danger/60 hover:bg-danger/15'
          : 'hover:border-border-strong hover:bg-surface-card-hover',
        'focus-visible:focus-ring',
        className,
      )}
      onClick={() => openFaq(topicId)}
    >
      {label}
    </button>
  )
}
